import { IScope } from "@/api";
import { styles } from "@/config/project";
import { alpha, Box, Button, keyframes, Typography } from "@mui/material";
import { AlertTriangle, FileX, HelpCircle, RefreshCw } from "lucide-react";

// Keyframe animations
const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const gentlePulse = keyframes`
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.7;
  }
`;

interface ScopeFailedStateProps {
    scope: IScope;
    onRetry?: () => void;
    onContactSupport?: () => void;
}

export const ScopeFailedState = ({
    scope,
    onRetry,
    onContactSupport,
}: ScopeFailedStateProps) => {
    const errorRed = "#dc2626";
    const errorRedLight = "#fef2f2";

    return (
        <Box
            sx={{
                width: "100%",
                height: "100%",
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "center",
                overflow: "auto",
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: { xs: 2, sm: 2.5, md: 3 },
                    maxWidth: 480,
                    width: "100%",
                    px: 4,
                    py: { xs: 4, sm: 5, md: 6 },
                    my: "auto",
                    animation: `${fadeIn} 0.4s ease-out`,
                }}
            >
                {/* Error illustration */}
                <Box
                    sx={{
                        position: "relative",
                        width: { xs: 88, sm: 100, md: 112 },
                        height: { xs: 110, sm: 125, md: 140 },
                        flexShrink: 0,
                    }}
                >
                    {/* Document shape with error state */}
                    <Box
                        sx={{
                            position: "absolute",
                            inset: 0,
                            bgcolor: "white",
                            borderRadius: 2.5,
                            boxShadow: `0 16px 40px ${alpha(errorRed, 0.12)}, 0 0 0 1px ${alpha(errorRed, 0.08)}`,
                            overflow: "hidden",
                            transition: "all 0.3s ease",
                        }}
                    >
                        {/* Document content lines - faded/broken appearance */}
                        {[...Array(7)].map((_, i) => (
                            <Box
                                key={i}
                                sx={{
                                    position: "absolute",
                                    left: 14,
                                    right: i % 3 === 0 ? 36 : 14,
                                    top: 18 + i * 14,
                                    height: 7,
                                    borderRadius: 1,
                                    bgcolor: alpha(
                                        "#94a3b8",
                                        i < 3 ? 0.25 : 0.12
                                    ),
                                    // Broken effect on some lines
                                    ...(i === 2 && {
                                        background: `linear-gradient(90deg, ${alpha("#94a3b8", 0.25)} 40%, transparent 40%, transparent 55%, ${alpha("#94a3b8", 0.25)} 55%)`,
                                    }),
                                    ...(i === 4 && {
                                        background: `linear-gradient(90deg, ${alpha("#94a3b8", 0.12)} 60%, transparent 60%)`,
                                    }),
                                }}
                            />
                        ))}

                        {/* Red diagonal cross overlay */}
                        <Box
                            sx={{
                                position: "absolute",
                                inset: 0,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                bgcolor: alpha(errorRedLight, 0.4),
                            }}
                        >
                            <FileX
                                size={40}
                                color={errorRed}
                                style={{ opacity: 0.6 }}
                            />
                        </Box>
                    </Box>

                    {/* Error badge */}
                    <Box
                        sx={{
                            position: "absolute",
                            top: -8,
                            right: -8,
                            width: 28,
                            height: 28,
                            borderRadius: "50%",
                            bgcolor: errorRed,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            boxShadow: `0 4px 12px ${alpha(errorRed, 0.4)}`,
                            animation: `${gentlePulse} 2s ease-in-out infinite`,
                        }}
                    >
                        <AlertTriangle
                            size={14}
                            color="white"
                            strokeWidth={2.5}
                        />
                    </Box>
                </Box>

                {/* Title and description */}
                <Box sx={{ textAlign: "center", flexShrink: 0 }}>
                    <Typography
                        variant="h6"
                        sx={{
                            fontWeight: 600,
                            color: "#1e293b",
                            mb: 1,
                            fontSize: {
                                xs: "1.125rem",
                                sm: "1.25rem",
                                md: "1.375rem",
                            },
                        }}
                    >
                        Scoping failed
                    </Typography>
                    <Typography
                        variant="body2"
                        sx={{
                            color: "#64748b",
                            maxWidth: 340,
                            mx: "auto",
                            lineHeight: 1.6,
                            fontSize: { xs: "0.875rem", sm: "0.9375rem" },
                        }}
                    >
                        We couldn't complete the analysis for this document.
                        This can happen with certain file types or complex
                        layouts.
                    </Typography>
                </Box>

                {/* Document name chip */}
                <Box
                    sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 1,
                        px: 2,
                        py: 1,
                        borderRadius: 2,
                        bgcolor: alpha("#f1f5f9", 0.8),
                        border: `1px solid ${alpha("#e2e8f0", 0.8)}`,
                        maxWidth: "100%",
                    }}
                >
                    <Box
                        sx={{
                            width: 6,
                            height: 6,
                            borderRadius: "50%",
                            bgcolor: errorRed,
                            flexShrink: 0,
                        }}
                    />
                    <Typography
                        variant="caption"
                        sx={{
                            color: "#475569",
                            fontWeight: 500,
                            fontSize: "0.8125rem",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                        }}
                    >
                        {scope.document_title}
                    </Typography>
                </Box>

                {/* Suggested actions box */}
                <Box
                    sx={{
                        width: "100%",
                        maxWidth: 360,
                        p: 2.5,
                        borderRadius: 2.5,
                        bgcolor: "white",
                        border: `1px solid ${alpha("#e2e8f0", 0.8)}`,
                        boxShadow: `0 1px 3px ${alpha("#000", 0.04)}`,
                    }}
                >
                    <Typography
                        variant="caption"
                        sx={{
                            display: "block",
                            fontWeight: 600,
                            color: "#64748b",
                            textTransform: "uppercase",
                            letterSpacing: "0.05em",
                            fontSize: "0.6875rem",
                            mb: 1.5,
                        }}
                    >
                        What you can try
                    </Typography>
                    <Box
                        component="ul"
                        sx={{
                            m: 0,
                            pl: 2.5,
                            "& li": {
                                color: "#475569",
                                fontSize: "0.8125rem",
                                lineHeight: 1.7,
                                mb: 0.5,
                                "&::marker": {
                                    color: "#94a3b8",
                                },
                            },
                        }}
                    >
                        <li>Re-upload the document and try again</li>
                        <li>Check that the PDF is not password-protected</li>
                        <li>Ensure the document contains readable text</li>
                    </Box>
                </Box>

                {/* Action buttons */}
                <Box
                    sx={{
                        display: "flex",
                        gap: 1.5,
                        flexWrap: "wrap",
                        justifyContent: "center",
                        flexShrink: 0,
                    }}
                >
                    {onRetry && (
                        <Button
                            variant="contained"
                            startIcon={<RefreshCw size={16} />}
                            onClick={onRetry}
                            sx={{
                                bgcolor: styles.primaryColor,
                                color: "white",
                                fontWeight: 600,
                                fontSize: "0.8125rem",
                                px: 2.5,
                                py: 1,
                                borderRadius: 2,
                                textTransform: "none",
                                boxShadow: `0 1px 2px ${alpha(styles.primaryColor, 0.2)}`,
                                "&:hover": {
                                    bgcolor: alpha(styles.primaryColor, 0.9),
                                    boxShadow: `0 4px 12px ${alpha(styles.primaryColor, 0.25)}`,
                                },
                            }}
                        >
                            Try Again
                        </Button>
                    )}
                    {onContactSupport && (
                        <Button
                            variant="outlined"
                            startIcon={<HelpCircle size={16} />}
                            onClick={onContactSupport}
                            sx={{
                                borderColor: "#e2e8f0",
                                color: "#475569",
                                fontWeight: 600,
                                fontSize: "0.8125rem",
                                px: 2.5,
                                py: 1,
                                borderRadius: 2,
                                textTransform: "none",
                                "&:hover": {
                                    borderColor: "#cbd5e1",
                                    bgcolor: "#f8fafc",
                                },
                            }}
                        >
                            Contact Support
                        </Button>
                    )}
                </Box>

                {/* Error code footer */}
                <Typography
                    variant="caption"
                    sx={{
                        color: "#94a3b8",
                        fontSize: "0.75rem",
                        mt: 1,
                        flexShrink: 0,
                    }}
                >
                    Error reference: SCOPE-{scope.id}
                </Typography>
            </Box>
        </Box>
    );
};
